import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import classNames from "classnames";
import Dialog from "@/components/Dialog";
import EmptyState from "@/components/EmptyState";
import KeyboardShortcutHint from "@/components/KeyboardShortcutHint";
import ListItem from "@/components/ListItem";
import SearchInput from "@/components/SearchInput";
import type { CommandPaletteDestination } from "@/lib/commandPaletteDestinations";
import { rankCommandPaletteDestinations } from "@/lib/commandPaletteRank";
import { resolveCommandPalettePath, type CommandPaletteResolveContext } from "@/lib/commandPaletteResolve";

export type CommandPaletteProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  destinations: CommandPaletteDestination[];
  resolveContext: CommandPaletteResolveContext;
  onNavigate: (path: string) => void;
};

function highlightMatchedChars(text: string, indices: number[]): string | JSX.Element {
  if (indices.length === 0 || !text) return text;

  const indexSet = new Set(indices);
  const parts: JSX.Element[] = [];
  let cursor = 0;

  while (cursor < text.length) {
    const isMatch = indexSet.has(cursor);
    let end = cursor + 1;
    while (end < text.length && indexSet.has(end) === isMatch) end += 1;
    const slice = text.slice(cursor, end);
    parts.push(
      isMatch ? (
        <span key={cursor} className="text-sys-brand-primary font-medium">
          {slice}
        </span>
      ) : (
        <span key={cursor}>{slice}</span>
      )
    );
    cursor = end;
  }

  return <>{parts}</>;
}

function formatBreadcrumbs(breadcrumbs: string[], breadcrumbIndices: number[][]): JSX.Element | null {
  if (breadcrumbs.length === 0) return null;

  return (
    <>
      {breadcrumbs.map((crumb, index) => (
        <span key={`${crumb}-${index}`}>
          {index > 0 ? " → " : null}
          {highlightMatchedChars(crumb, breadcrumbIndices[index] ?? [])}
        </span>
      ))}
    </>
  );
}

function isComposingKey(event: KeyboardEvent<HTMLInputElement>): boolean {
  return event.nativeEvent.isComposing || event.keyCode === 229;
}

export default function CommandPalette({
  open,
  onOpenChange,
  destinations,
  resolveContext,
  onNavigate
}: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [wasOpen, setWasOpen] = useState(open);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectedRef = useRef<HTMLDivElement | null>(null);
  const listboxId = useId();

  if (open !== wasOpen) {
    setWasOpen(open);
    setQuery("");
    setSelectedIndex(0);
  }

  const results = useMemo(() => rankCommandPaletteDestinations(destinations, query), [destinations, query]);
  const selectedIndexSafe = results.length === 0 ? 0 : Math.min(selectedIndex, results.length - 1);
  const activeOptionId = results.length > 0 ? `${listboxId}-option-${selectedIndexSafe}` : undefined;

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedIndexSafe, results]);

  const goToResult = useCallback(
    (index: number) => {
      const destination = results[index];
      if (!destination) return;
      const path = resolveCommandPalettePath(destination.path, resolveContext, destination.fallbackPath);
      onNavigate(path);
      onOpenChange(false);
    },
    [onNavigate, onOpenChange, resolveContext, results]
  );

  const handleQueryChange = useCallback((value: string) => {
    setQuery(value);
    setSelectedIndex(0);
  }, []);

  const handleSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (isComposingKey(event)) return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSelectedIndex((current) => Math.min(current + 1, Math.max(0, results.length - 1)));
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedIndex((current) => Math.max(current - 1, 0));
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        goToResult(selectedIndexSafe);
      }
    },
    [goToResult, results.length, selectedIndexSafe]
  );

  const hasResults = results.length > 0;

  return (
    <Dialog
      open={open}
      onClosed={() => {
        setQuery("");
        setSelectedIndex(0);
        onOpenChange(false);
      }}
      headline="Go to Page"
      hasFormElements
      className="w-[min(640px,calc(100vw-2rem))] p-6 pr-2"
      actions={
        <span className="text-body-small text-sys-brand-on-surface-variant flex items-center gap-2">
          <span>↑↓ to navigate · Enter to open</span>
          <KeyboardShortcutHint keyLabel="P" implicitShift testId="command-palette-shortcut-hint" />
        </span>
      }
    >
      {open ? (
        <div className="flex flex-col gap-6 pr-4">
          <SearchInput
            ref={inputRef}
            query={query}
            onChange={handleQueryChange}
            placeholder="Search Pages"
            ariaLabel="Search Pages"
            pageSearchShortcut={false}
            listboxId={listboxId}
            listboxExpanded={hasResults}
            activeOptionId={activeOptionId}
            onKeyDown={handleSearchKeyDown}
          />
          <div
            id={listboxId}
            role={hasResults ? "listbox" : "status"}
            aria-label={hasResults ? "Pages" : undefined}
            className="max-h-[min(50vh,420px)] overflow-y-auto"
            data-testid={hasResults ? "command-palette-results" : "command-palette-empty"}
          >
            {hasResults ? (
              results.map((destination, index) => {
                const selected = index === selectedIndexSafe;
                const breadcrumbs =
                  destination.breadcrumbs.length > 0
                    ? formatBreadcrumbs(destination.breadcrumbs, destination.match.breadcrumbIndices)
                    : null;

                return (
                  <div
                    key={destination.id}
                    id={`${listboxId}-option-${index}`}
                    ref={selected ? selectedRef : undefined}
                    role="option"
                    aria-selected={selected}
                    data-testid={`command-palette-result-${index}`}
                    className={classNames(
                      "rounded-lg cursor-pointer",
                      selected && "bg-sys-brand-surface-container-high"
                    )}
                    onMouseEnter={() => setSelectedIndex(index)}
                    onClick={() => goToResult(index)}
                  >
                    <ListItem
                      compact
                      headline={highlightMatchedChars(destination.label, destination.match.labelIndices)}
                      supportingText={breadcrumbs}
                    />
                  </div>
                );
              })
            ) : (
              <EmptyState title="No matching pages" caption="Try a different search" />
            )}
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}
